import React, { FC, ReactElement, useCallback, useState } from 'react';
import { AutoSizer, List, ListRowProps } from 'react-virtualized';
import './dynamic-list.scss';
import { Filters } from '../filters/filters';
import { NoResult } from '../no-result/no-result';
import { FilterConfig } from 'src/model/filter';

interface Field<T> {
    key: string;
    display?: (item: T) => ReactElement;
}

interface DynamicListProps<T> {
    list: T[];
    title: string;
    filterConfig: FilterConfig<T>;
    fields?: Field<T>[];
    onRowClick: (item: T) => void;
    isActiveRow?: (item: T) => boolean;
}

const rowHeight = 55;

const DynamicList: FC<DynamicListProps<any>> = ({ list, title, filterConfig, fields, onRowClick, isActiveRow }) => {
    const [filteredList, setFilteredList] = useState<any[]>([]);

    const rowRenderer = useCallback(({ key, index, style }: ListRowProps) => {
        const item = filteredList[index];
        const isActive = isActiveRow?.(item);
        return (
            <button
                key={key}
                type="button"
                className={`row ${isActive ? 'active' : ''}`}
                style={{...style}}
                onClick={() => onRowClick(item)}
            >
                {fields?.map(({ key, display }) => display?.(item) ?? item[key])}
            </button>
        );
    }, [filteredList, fields, onRowClick, isActiveRow]);

    return (
        <div className="dynamic-list-wrapper">
            <div className="dynamic-list-header">
                <div className="dynamic-list-title">
                    {title}
                </div>
                <Filters filterConfig={filterConfig}
                         setData={setFilteredList}
                         originalData={list} />
            </div>
            <div className="dynamic-list-content">
                {filteredList.length ? (
                    <AutoSizer>
                        {({width}) => (
                            <List
                                className="dynamic-list-content"
                                width={width}
                                height={filteredList.length * rowHeight}
                                rowCount={filteredList.length}
                                rowHeight={rowHeight}
                                rowRenderer={rowRenderer}
                            />
                        )}
                    </AutoSizer>
                ) : (
                    <NoResult />
                )}
            </div>
        </div>
    );
};

export default DynamicList;